import { ChevronDown } from 'lucide-react'
import type { CustomAgentResultRevision } from '@axilon-apps/domains-ontology-ui-state'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu'
import { cn } from '../../lib/utils'
import { formatCompactTime } from './run-timing'

export function revisionCreatedAt(
  revisions: readonly CustomAgentResultRevision[] | undefined,
  revision: number
): string | undefined {
  return revisions?.find((row) => row.revision === revision)?.created_at
}

/** Prefer persisted snapshots; include `currentRevision` if GET is ahead of the metas. */
export function pickerVersions(
  currentRevision: number,
  revisions?: readonly CustomAgentResultRevision[]
): number[] {
  const fromMeta = [...new Set((revisions ?? []).map((row) => row.revision).filter((n) => n >= 1))]
  if (currentRevision > 0 && !fromMeta.includes(currentRevision)) fromMeta.push(currentRevision)
  if (fromMeta.length > 0) return fromMeta.sort((a, b) => b - a)
  return Array.from({ length: Math.max(currentRevision, 0) }, (_, i) => currentRevision - i)
}

interface Props {
  currentRevision: number
  selectedRevision: number
  revisions?: readonly CustomAgentResultRevision[]
  onSelect: (revision: number) => void
}

export function FollowUpVersionPicker({
  currentRevision,
  selectedRevision,
  revisions,
  onSelect
}: Props) {
  const isCurrent = selectedRevision === currentRevision
  const selectedAt = revisionCreatedAt(revisions, selectedRevision)
  const versions = pickerVersions(currentRevision, revisions)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={
            isCurrent
              ? `Current revision ${currentRevision} ${formatCompactTime(selectedAt)}`
              : `Revision ${selectedRevision} ${formatCompactTime(selectedAt)}`
          }
          className={cn(
            'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md border px-2.5',
            'font-mono text-11 text-ink-2',
            isCurrent ? 'border-line bg-bg-elev' : 'border-brand bg-brand-soft text-brand'
          )}
        >
          <span className="font-sans text-12 font-medium">
            {isCurrent ? 'Current' : formatCompactTime(selectedAt)}
          </span>
          <span className="opacity-70">
            {isCurrent ? `Rev ${currentRevision}` : `Rev ${selectedRevision}`}
          </span>
          <ChevronDown className="size-3 opacity-70" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-65 p-0">
        <DropdownMenuLabel className="border-b border-line px-3 py-1.5 font-mono text-10 font-semibold uppercase tracking-[0.12em] text-ink-3">
          Finding versions
        </DropdownMenuLabel>
        {versions.map((rev) => {
          const current = rev === currentRevision
          const at = revisionCreatedAt(revisions, rev)
          return (
            <DropdownMenuItem
              key={rev}
              aria-label={
                current
                  ? `Current revision ${rev} ${formatCompactTime(at)}`
                  : `Revision ${rev} ${formatCompactTime(at)}`
              }
              onSelect={() => onSelect(rev)}
              className="items-start gap-2 rounded-none px-3 py-2"
            >
              <span
                className={cn(
                  'mt-1.5 size-1.5 shrink-0 rounded-full',
                  current ? 'bg-st-run' : 'bg-ink-4'
                )}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium text-ink">
                  {current ? 'Current' : formatCompactTime(at)}
                </span>
                <span className="block font-mono text-11 text-ink-3">
                  {current ? `Rev ${rev} · ${formatCompactTime(at)}` : `Rev ${rev}`}
                </span>
              </span>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
